import { css, Theme } from '@emotion/react';
import { PencilSimple } from '@phosphor-icons/react';
import { Help } from 'client/components/Help';
import { Stack } from 'client/components/Stack';
import { Tag } from 'client/components/Tag';
import { Typography } from 'client/components/Typography';
import { ComponentIcon } from 'client/sections/components/ComponentIcon';

export interface StackContentListProps {
  title: JSX.Element;
  items: {
    label: string;
    component: { identifier: string; name: string; package: string };
  }[];
  prefix?: JSX.Element;
  postfix?: JSX.Element;
  help?: string;
  onClick: (component: { identifier: string; package: string }) => void;
}

export const StackContentList = ({ title, items, prefix, postfix, help, onClick }: StackContentListProps) => (
  <section>
    <Typography variant="body1" css={titleStyle}>
      {title}
    </Typography>
    <Stack gap={6} component="ul" css={filesStyle}>
      {prefix}
      {items.map((item, index) => (
        <li role="button" key={`${item.component.identifier}-${index}`} onClick={() => onClick(item.component)}>
          <div>
            <Tag>
              <Typography variant="body1">{item.label}</Typography> <PencilSimple weight="fill" />
            </Tag>
          </div>
          <div css={nameStyle}>
            <ComponentIcon url={item.component.package} type="component" size={16} hideType />
            <Typography variant="body1">{item.component.name}</Typography>
          </div>
        </li>
      ))}
      {postfix}
    </Stack>
    {help && <Help>{help}</Help>}
  </section>
);

const titleStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold !important;

  > svg {
    color: ${theme.color.grey[80]};
    transform: translateY(1px);
  }
`;

const filesStyle = (theme: Theme) => css`
  position: relative;
  margin: 0;
  margin-top: 8px;
  margin-left: 9px;
  padding: 0;
  list-style: none;
  border-left: 2px solid ${theme.color.grey[60]};

  > svg {
    position: absolute;
    top: -32px;
    color: ${theme.color.white};
    transform: translateX(-50%);
  }

  > li {
    display: flex;
    align-items: center;
    font-family: ${theme.font.code};
    font-size: 16px;
    color: white;
    user-select: none;
    cursor: pointer;

    &[aria-disabled] {
      cursor: initial;
    }

    > div,
    span {
      flex: 1;
    }

    label {
      display: inline-flex;
      align-items: center;
      gap: 8px;

      .body1 {
        font-family: ${theme.font.code};
      }
    }

    &::before {
      content: '';
      display: block;
      margin-right: 6px;
      width: 12px;
      height: 2px;
      background-color: ${theme.color.grey[60]};
    }
  }
`;

const nameStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
`;
